import type React from 'react';
import { useSelector } from 'react-redux';

// Constellation
import { Box, Button, Heading, Image, Paragraph, TextButton } from '@zillow/constellation-10';

// Components
import LazyLoading from 'app/shared/components/LazyLoad';

// Styles
import { containerStyles, imageContainerBaseStyles, imageContainerMwebStyles } from './styles';

// Types
import type { State } from 'app/shared/types/State';

// Utils
import optimizelyUtils from 'app/shared/utils/analytics/optimizelyUtils';

type SlideProps = {
  body: string | React.ReactElement;
  btnPrimaryHref: string;
  btnPrimaryLabel: string;
  btnSecondaryHref?: string;
  btnSecondaryLabel?: string;
  heading: string;
  imgPosition: 'left' | 'right';
  srcBackground: string;
  srcOverlay?: string;
};

const Slide = ({
  body,
  btnPrimaryHref,
  btnPrimaryLabel,
  btnSecondaryHref,
  btnSecondaryLabel,
  heading,
  imgPosition,
  srcBackground,
  srcOverlay,
}: SlideProps) => {
  const device = useSelector((state: State) => state.app.device);
  const isMobileOrTablet = device.isMobile || device.screenWidth === 'md' || device.screenWidth === 'lg';

  const trackBtnPrimaryClick = () => {
    optimizelyUtils.track();
  };

  const renderImg = () =>
    isMobileOrTablet ? (
      <Box css={{ ...imageContainerMwebStyles, padding: !srcOverlay ? '600' : undefined }}>
        {srcOverlay ? (
          <LazyLoading>
            <Image
              src={srcOverlay}
              css={{ maxWidth: '100%', verticalAlign: 'middle', height: '100%' }}
              alt="Overlay image"
            />
          </LazyLoading>
        ) : (
          <LazyLoading>
            <Image
              src={srcBackground}
              alt="Background image"
              css={{
                boxShadow: 'rgba(130, 130, 130, 0.8) 0px 0px 20px',
                maxHeight: '100%',
              }}
            />
          </LazyLoading>
        )}
      </Box>
    ) : (
      <Box
        css={{
          ...imageContainerBaseStyles,
          marginLeft: imgPosition === 'right' ? '1000' : '',
          marginRight: imgPosition === 'left' ? '1000' : '',
          justifyContent: imgPosition === 'left' ? 'flex-start' : 'flex-end',
        }}
      >
        <LazyLoading>
          <Box
            // oxlint-disable-next-line constellation/no-raw-spacing-values -- `width: 390px` is past the end of the numeric sizes scale, which stops at `5000` (200px); `height: 270px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
            css={{ width: '390px', height: '270px', backgroundSize: 'cover' }}
            style={{ backgroundImage: `url(${srcBackground})` }}
          />
        </LazyLoading>
        {srcOverlay && (
          <Box
            css={{
              position: 'absolute',
              top: 0,
              // oxlint-disable-next-line constellation/no-raw-spacing-values -- `width: 256px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
              width: '256px',
              ...(imgPosition === 'left' ? { right: 0 } : { left: 0 }),
            }}
          >
            <LazyLoading>
              <Image
                src={srcOverlay}
                css={{ maxWidth: '100%', verticalAlign: 'middle', height: '100%' }}
                alt="Overlay image"
              />
            </LazyLoading>
          </Box>
        )}
      </Box>
    );

  const renderText = () => (
    // oxlint-disable-next-line constellation/no-raw-spacing-values -- `maxWidth: 615px` is past the end of the numeric sizes scale, which stops at `5000` (200px)
    <Box css={{ flex: 1, maxWidth: '615px' }}>
      <Heading level={3} textStyle="responsive.heading-md" css={{ marginBottom: '400' }}>
        {heading}
      </Heading>
      <Paragraph css={{ marginBottom: '600' }}>{body}</Paragraph>
      <Button asChild tone="brand" emphasis="filled" size="sm" onClick={trackBtnPrimaryClick}>
        <a href={btnPrimaryHref}>{btnPrimaryLabel}</a>
      </Button>
      {btnSecondaryLabel && (
        <TextButton asChild css={{ marginLeft: '600' }}>
          <a href={btnSecondaryHref}>{btnSecondaryLabel}</a>
        </TextButton>
      )}
    </Box>
  );
  return (
    <Box css={containerStyles}>
      {imgPosition === 'left' || isMobileOrTablet ? (
        <>
          {renderImg()}
          {renderText()}
        </>
      ) : (
        <>
          {renderText()}
          {renderImg()}
        </>
      )}
    </Box>
  );
};

export default Slide;
